import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, Divider, Input, Text, dialogService, toastService } from '@thefarmersfront/kitchen';

import { BrandLogo, InfoText } from '~/components/common';

import { useAuthState, usePostUserLoginCheckTwoFactorCode, usePostUserLoginSendTwoFactorCode } from '~/hooks/auth';
import { useInterval } from '~/hooks/common';
import { authChannelService, authDBService } from '~/services';
import { useLoadingStore } from '~/store';
import { consumeLoginRedirectPath, getDecodedJwtValue, validateNumberOnly } from '~/utils';

import { getDefaultRoute } from '~/configs';
import {
  AUTH_CHANNEL_MESSAGE,
  AUTH_CODE_EXPIRATION_TIME,
  AUTH_CODE_RESEND_DELAY,
  DEFAULT_INPUT_STATUS,
  PATH,
  TOAST_MESSAGE,
  TWO_FACTOR_AUTH,
} from '~/constants';

import type { TypeErrorResponseData, TypeInputState } from '~/types/common.types';

import styles from './TwoFactorAuthPage.module.scss';

const TwoFactorAuthPage = () => {
  const navigate = useNavigate();

  const { postUserLoginSendTwoFactorCode } = usePostUserLoginSendTwoFactorCode();
  const { postUserLoginCheckTwoFactorCode } = usePostUserLoginCheckTwoFactorCode();
  const { updateIsAuthenticated } = useAuthState();

  const twoFactorId = useRef('');
  const isRememberIdChecked = useRef(false);

  const { setIsLoading } = useLoadingStore();

  const [loginId, setLoginId] = useState('');

  const [isInitialAuthCodeEmailSent, setIsInitialAuthCodeEmailSent] = useState(false);

  const [authCodeInputValue, setAuthCodeInputValue] = useState('');
  const [authCodeInputState, setAuthCodeInputState] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);

  const [authCodeExpiryEndTime, setAuthCodeExpiryEndTime] = useState(0);
  const [authCodeExpiryTimeLeft, setAuthCodeExpiryTimeLeft] = useState(0);

  const isAuthCodeEmailResendable = authCodeExpiryTimeLeft <= AUTH_CODE_EXPIRATION_TIME - AUTH_CODE_RESEND_DELAY;

  useEffect(() => {
    const initUserInfo = async () => {
      const twoFactorAuthInfo = await authDBService.getTwoFactorAuthInfo();

      const id = twoFactorAuthInfo?.login_id ?? '';
      twoFactorId.current = twoFactorAuthInfo?.two_factor_id ?? '';
      isRememberIdChecked.current = twoFactorAuthInfo?.is_remember_id ?? false;

      if (!id) {
        dialogService.addDialog({
          type: 'alert',
          children: <span>잘못된 접근 경로입니다.</span>,
          cancelText: '확인',
          onConfirm: async () => {
            await authDBService.removeTwoFactorAuthInfo();

            navigate(PATH.LOGIN);
          },
        });
      }

      setLoginId(id);
    };

    initUserInfo();
  }, []);

  const calculateTimeLeft = () => {
    const currentTime = new Date().getTime();
    return authCodeExpiryEndTime - currentTime;
  };

  useInterval(
    () => {
      setAuthCodeExpiryTimeLeft(calculateTimeLeft());
    },
    1000,
    isInitialAuthCodeEmailSent && authCodeExpiryTimeLeft > 0
  );

  const fetchUserLoginSendTwoFactorCodeMail = async () => {
    try {
      setIsLoading(true);

      const params = {
        loginId: loginId,
        twoFactorId: twoFactorId.current,
      };

      await postUserLoginSendTwoFactorCode(params);

      toastService.addToast({
        message: '인증코드가 발송되었습니다.',
      });

      if (!isInitialAuthCodeEmailSent) {
        setIsInitialAuthCodeEmailSent(true);
      }

      setAuthCodeExpiryEndTime(new Date().getTime() + AUTH_CODE_EXPIRATION_TIME);
      setAuthCodeExpiryTimeLeft(AUTH_CODE_EXPIRATION_TIME);
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserLoginCheckTwoFactorCode = async () => {
    try {
      setIsLoading(true);

      const params = {
        loginId: loginId,
        twoFactorId: twoFactorId.current,
        twoFactorCode: authCodeInputValue,
      };

      const data = await postUserLoginCheckTwoFactorCode(params);

      await authDBService.saveLoginInfo({
        login_id: data.loginId,
        access_token: data.token,
        is_remember_id: isRememberIdChecked.current,
      });

      await updateIsAuthenticated();

      authChannelService.postMessage(AUTH_CHANNEL_MESSAGE.LOGIN);

      toastService.addToast({
        message: '정상적으로 로그인되었습니다.',
      });

      await authDBService.removeTwoFactorAuthInfo();

      const decodedJwtValue = await getDecodedJwtValue();

      navigate(
        consumeLoginRedirectPath(
          decodedJwtValue ? getDefaultRoute(decodedJwtValue.ownerType, decodedJwtValue.logisticsTypes) : PATH.MAIN
        )
      );
    } catch (error) {
      const errorCode = (error as TypeErrorResponseData['error'])?.code;
      const errorMessage = (error as TypeErrorResponseData['error'])?.message;

      if (errorCode === TWO_FACTOR_AUTH.TWO_FACTOR_AUTH_CODE_TIME_EXPIRED) {
        setAuthCodeInputState({
          status: 'error',
          message: '인증코드 유효시간이 만료되었습니다.\n인증코드를 재전송하여 다시 시도해주세요.',
        });

        return;
      } else if (errorCode === TWO_FACTOR_AUTH.TWO_FACTOR_AUTH_CODE_NOT_MATCHED) {
        setAuthCodeInputState({
          status: 'error',
          message: '인증코드가 일치하지 않습니다.',
        });

        return;
      }

      toastService.addToast({
        type: 'error',
        message: errorMessage || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeAuthCodeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!validateNumberOnly(e.target.value)) {
      return;
    }

    setAuthCodeInputValue(e.target.value);

    if (authCodeExpiryTimeLeft !== 0) {
      setAuthCodeInputState(DEFAULT_INPUT_STATUS);
    }
  };

  const handleBlurAuthCodeInput = () => {
    setAuthCodeInputValue(authCodeInputValue.trim());
  };

  const handleClickSendAuthCode = () => {
    fetchUserLoginSendTwoFactorCodeMail();
  };

  const handleClickReSendAuthCode = () => {
    fetchUserLoginSendTwoFactorCodeMail();

    setAuthCodeInputValue('');
    setAuthCodeInputState(DEFAULT_INPUT_STATUS);
  };

  const handleClickGoBack = async () => {
    await authDBService.removeTwoFactorAuthInfo();

    navigate(PATH.LOGIN);
  };

  const handleSubmitAuthCode = async () => {
    const trimmedAuthCode = authCodeInputValue.trim();

    if (authCodeExpiryTimeLeft === 0) {
      setAuthCodeInputState({
        status: 'error',
        message: '인증코드 유효시간이 만료되었습니다.\n인증코드를 재전송하여 다시 시도해주세요.',
      });

      return;
    } else if (trimmedAuthCode.length === 0) {
      setAuthCodeInputState({
        status: 'error',
        message: '인증코드를 입력해 주세요.',
      });

      return;
    }

    fetchUserLoginCheckTwoFactorCode();
  };

  const handleKeyDownAuthCodeInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmitAuthCode();
    }
  };

  return (
    <div className={styles['two-factor-auth-page-container']}>
      <div className={styles['two-factor-auth-container']}>
        <BrandLogo className={styles['kls-logo']} width={147} height={19} />
        <Text className={styles['two-factor-auth-title']} size="accessibility1" weight="bold" element="p">
          2단계 이메일 인증
        </Text>
        <div className={styles['two-factor-auth-user-id']}>
          <Text size="large" element="p" color="secondary">
            로그인 아이디
          </Text>
          <Text size="large" weight="semi-bold" element="p">
            {loginId}
          </Text>
        </div>
        <Text size="large" element="p" color="tertiary">
          시스템에 등록된 이메일 주소로 인증코드를 발송합니다.
        </Text>
        <Divider className={styles['divider']} color="gray100" />
        <div className={styles['two-factor-auth-code-inputs']}>
          <Text size="large" element="p" color="secondary">
            인증코드
          </Text>
          {isInitialAuthCodeEmailSent ? (
            <div className={styles['two-factor-auth-code-input']}>
              <Input
                type="text"
                placeholder="인증코드 6자 입력"
                maxLength={10}
                timerTime={Math.floor(authCodeExpiryTimeLeft / 1000)}
                status={authCodeInputState.status}
                validationMessage={authCodeInputState.message}
                value={authCodeInputValue}
                onChange={handleChangeAuthCodeInput}
                onBlur={handleBlurAuthCodeInput}
                onKeyDown={handleKeyDownAuthCodeInput}
              />
              <Button color="tertiary" disabled={!isAuthCodeEmailResendable} onClick={handleClickReSendAuthCode}>
                재전송
              </Button>
            </div>
          ) : (
            <Button full onClick={handleClickSendAuthCode}>
              이메일로 인증코드 전송
            </Button>
          )}
        </div>
        <div className={styles['two-factor-auth-description']}>
          <InfoText>이메일이 수신되지 않은 경우 스팸 메일함을 확인해 주세요.</InfoText>
          <InfoText>이메일 확인이 불가한 경우 컬리 담당자에게 별도 문의 부탁드립니다.</InfoText>
        </div>
        <div className={styles['two-factor-auth-code-actions']}>
          <Button full variant="outlined" color="tertiary" onClick={handleClickGoBack}>
            이전으로
          </Button>
          <Button full color="secondary" disabled={!isInitialAuthCodeEmailSent} onClick={handleSubmitAuthCode}>
            인증 확인
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuthPage;
